// frontend/src/components/userDetails/tabs/APISettingsTab.jsx
import React, { useEffect, useState } from "react";
import { Form, Button, Alert, InputGroup, Spinner } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axiosClient from "../../../api/axiosClient";

const APISettingsTab = ({ userId }) => {
  const numericUserId = Number(userId);

  const [apiSettings, setApiSettings] = useState({
    endpoint_url: "",
    username: "",
    password: "",
    domain: "",
  });

  const [message, setMessage] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [existingPassword, setExistingPassword] = useState(null); // store old encrypted password

  // Load API settings when component mounts or userId changes
  useEffect(() => {
    const loadApiSettings = async () => {
      if (!(numericUserId > 0)) return;

      try {
        const res = await axiosClient.get(`/api-settings/${numericUserId}`);
        if (res.data) {
          setApiSettings({
            endpoint_url: res.data.endpoint_url || "",
            username: res.data.username || "",
            password: "", // never show encrypted password
            domain: res.data.domain || "",
          });
          setExistingPassword(res.data.password || null); // keep old encrypted password
        } else {
          setApiSettings({ endpoint_url: "", username: "", password: "", domain: "" });
          setExistingPassword(null);
        }
      } catch (err) {
        console.log("No API settings yet.", err.message);
        setApiSettings({ endpoint_url: "", username: "", password: "", domain: "" });
        setExistingPassword(null);
      }
    };

    loadApiSettings();
  }, [numericUserId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setApiSettings((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const isValid = () => {
    return apiSettings.endpoint_url.trim() && apiSettings.username.trim();
  };

  const handleSave = async () => {
    if (!isValid() || !(numericUserId > 0)) {
      return setMessage({
        text: "Please select a user and fill in Endpoint URL and Username.",
        type: "warning",
      });
    }

    setLoading(true);
    try {
      // If password field is empty, keep old encrypted password
      const payload = {
        endpoint_url: apiSettings.endpoint_url,
        username: apiSettings.username,
        password: apiSettings.password || existingPassword,
        domain: apiSettings.domain || null,
      };

      const res = await axiosClient.post(`/api-settings/${numericUserId}`, payload);

      setMessage({
        text: res.data.message || "API Settings saved successfully!",
        type: "success",
      });

      // Clear password field after save
      setApiSettings((prev) => ({ ...prev, password: "" }));
    } catch (err) {
      console.error(err.response || err.message);
      setMessage({
        text:
          err.response?.data?.message ||
          "Failed to save API settings. Check the user and inputs.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!(numericUserId > 0)) return;
    if (!window.confirm("Are you sure you want to delete these API settings?")) return;

    setLoading(true);
    try {
      const res = await axiosClient.delete(`/api-settings/${numericUserId}`);
      setApiSettings({ endpoint_url: "", username: "", password: "", domain: "" });
      setExistingPassword(null);
      setMessage({
        text: res.data.message || "API settings deleted successfully.",
        type: "success",
      });
    } catch (err) {
      console.error(err.response || err.message);
      setMessage({
        text: err.response?.data?.message || "Failed to delete API settings.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3">
      <h5 className="mb-4">API Service Settings</h5>

      {message.text && (
        <Alert
          variant={message.type}
          onClose={() => setMessage({ text: "", type: "" })}
          dismissible
        >
          {message.text}
        </Alert>
      )}

      <Form>
        <Form.Group className="mb-3">
          <Form.Label>
            Endpoint URL <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            placeholder="https://example.com/api"
            name="endpoint_url"
            value={apiSettings.endpoint_url}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Username <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            placeholder="Enter API username"
            name="username"
            value={apiSettings.username}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <InputGroup>
            <Form.Control
              placeholder="Enter API password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={apiSettings.password}
              onChange={handleChange}
            />
            <InputGroup.Text
              style={{ cursor: "pointer" }}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </InputGroup.Text>
          </InputGroup>
          <Form.Text className="text-muted">
            Leave blank to keep the existing password.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Domain (Optional)</Form.Label>
          <Form.Control
            placeholder="Domain (if needed)"
            name="domain"
            value={apiSettings.domain}
            onChange={handleChange}
          />
        </Form.Group>

        <div className="d-flex gap-2">
          <Button
            onClick={handleSave}
            disabled={loading} // only disable while saving
            className="d-flex align-items-center gap-2"
          >
            {loading && <Spinner animation="border" size="sm" />}
            {loading ? "Saving..." : "Save API Settings"}
          </Button>

          <Button
            variant="danger"
            disabled={loading || !(numericUserId > 0)}
            onClick={handleDelete}
            className="d-flex align-items-center gap-2"
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Delete API Settings"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default APISettingsTab;
